import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  GetSingleSubCategoryThunk,
  AllCategoryThunk,
  UpdateSubCategoryThunk,
  AddSubCategoryThunk,
} from "../../redux/states/Category/thunk";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import {
  Upload,
  Save,
  ArrowLeft,
  Loader2,
  Trash2,
  Image as ImageIcon,
} from "lucide-react";

const CreateUpdateSubCategory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { categories, subsingleCategory, loading, error } = useSelector(
    (state) => state.categories
  );
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    status: "active",
    image: null,
  });

  console.log(subsingleCategory);

  const [preview, setPreview] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [slugAutoGenerated, setSlugAutoGenerated] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");

  // Load data if editing
  useEffect(() => {
    if (uuid) {
      setIsUpdating(true);
      dispatch(GetSingleSubCategoryThunk(uuid));
    }
  }, [uuid, dispatch]);

  useEffect(() => {
    dispatch(AllCategoryThunk());
  }, [dispatch]);

  useEffect(() => {
    if (subsingleCategory && isUpdating) {
      setFormData({
        name: subsingleCategory.name || "",
        slug: subsingleCategory.slug || "",
        status: subsingleCategory.status || "active",
        image: null,
      });
      if (subsingleCategory.image) {
        setPreview(
          `${process.env.REACT_APP_API_URL}/${subsingleCategory.image}`
        );
      }
    }
  }, [subsingleCategory, isUpdating]);

  // Auto-generate slug from name
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "name" && !slugAutoGenerated) {
      const newSlug = generateSlug(value);
      setFormData((prev) => ({
        ...prev,
        name: value,
        slug: newSlug,
      }));
    } else if (name === "name" && slugAutoGenerated) {
      setFormData((prev) => ({
        ...prev,
        name: value,
      }));
    } else if (files && files[0]) {
      setFormData({ ...formData, [name]: files[0] });
      setPreview(URL.createObjectURL(files[0]));
    } else {
      setFormData({ ...formData, [name]: value });
    }

    // If user manually edits slug, stop auto-generation
    if (name === "slug") {
      setSlugAutoGenerated(true);
    }
  };

  // Handle main category selection
  const handleCategorySelect = (e) => {
    setSelectedCategory(e.target.value);
  };

  // Handle slug generation manually
  const handleGenerateSlug = () => {
    const newSlug = generateSlug(formData.name);
    setFormData((prev) => ({
      ...prev,
      slug: newSlug,
    }));
    setSlugAutoGenerated(true);
  };

  // Remove image
  const handleRemoveImage = () => {
    setFormData((prev) => ({ ...prev, image: null }));
    setPreview(null);
  };

  // Submit form
  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate form
    if (!formData.name.trim()) {
      alert("Please enter the category name");
      return;
    }

    if (!formData.slug.trim()) {
      alert("Please enter the slug");
      return;
    }

    if (!selectedCategory) return alert("Please select a main category");

    const data = new FormData();

    // Append all form data
    data.append("name", formData.name);
    data.append("slug", formData.slug);
    data.append("status", formData.status);
    data.append("category_id", selectedCategory); // Send main category UUID

    // Append image only if it's a new file
    if (formData.image instanceof File) {
      data.append("image", formData.image);
    }

    // Append UUID for update
    if (isUpdating) {
      data.append("uuid", uuid);
    }

    if (isUpdating) {
      dispatch(UpdateSubCategoryThunk({ uuid, formData: data })).then(
        navigate("/sub-category")
      );
    } else {
      dispatch(AddSubCategoryThunk(data)).then((result) => {
        navigate("/sub-category");
      });
    }
  };

  return (
    <NonAuthLayout>
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-12xl mx-auto">
          {/* Header Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="mb-4 sm:mb-0">
                <h1 className="text-3xl font-bold text-gray-800">
                  {isUpdating ? "Update Category" : "Add New Category"}
                </h1>
                <p className="text-gray-600 mt-2">
                  {isUpdating
                    ? "Update the existing category information"
                    : "Add a new category to the system"}
                </p>
              </div>
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors px-4 py-2 border border-gray-300 rounded-lg hover:border-blue-600"
              >
                <ArrowLeft className="w-4 h-4" />
                Go Back
              </button>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-8">
              <div className="flex items-center">
                <div className="bg-red-100 p-2 rounded-lg mr-4">
                  <Trash2 className="text-red-600 text-xl" />
                </div>
                <div>
                  <h3 className="text-red-800 font-semibold text-lg">Error!</h3>
                  <p className="text-red-600">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6 pb-3 border-b border-gray-200">
                Basic Information
              </h2>

              <div className="space-y-6">
                {/* Name Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-lg"
                    placeholder="Example: Groceries & Essentials"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Enter the full name of the category
                  </p>
                </div>

                {
                  <div>
                    <label className="block mb-2 font-medium">
                      Main Category *
                    </label>
                    <select
                      value={selectedCategory}
                      onChange={handleCategorySelect}
                      className="w-full border px-3 py-2 rounded-lg"
                    >
                      <option value="">-- Select Main Category --</option>
                      {categories &&
                        categories?.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                    </select>
                  </div>
                }

                <div>
                  <label className="block mb-2 font-medium">Status *</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full border px-3 py-2 rounded-lg"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                {/* Slug Field */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Slug (URL) *
                    </label>
                    <button
                      type="button"
                      onClick={handleGenerateSlug}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Auto Generate
                    </button>
                  </div>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      name="slug"
                      value={formData.slug}
                      onChange={handleChange}
                      required
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                      placeholder="Example: groceries-essentials"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    This will appear in the URL. Use only lowercase letters,
                    numbers, and hyphens
                  </p>
                </div>

                {/* Status Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status *
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <label
                      className={`relative flex cursor-pointer rounded-xl border p-4 focus:outline-none ${
                        formData.status === "active"
                          ? "border-blue-500 bg-blue-50 ring-2 ring-blue-500"
                          : "border-gray-300 bg-white"
                      }`}
                    >
                      <input
                        type="radio"
                        name="status"
                        value="active"
                        checked={formData.status === "active"}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <div className="flex w-full items-center justify-between">
                        <div className="flex items-center">
                          <div className="text-sm">
                            <span className="font-medium text-gray-900">
                              Active
                            </span>
                            <p className="text-gray-500">
                              Category will be visible
                            </p>
                          </div>
                        </div>
                        <div
                          className={`h-4 w-4 rounded-full border-2 ${
                            formData.status === "active"
                              ? "border-blue-500 bg-blue-500"
                              : "border-gray-300"
                          }`}
                        ></div>
                      </div>
                    </label>

                    <label
                      className={`relative flex cursor-pointer rounded-xl border p-4 focus:outline-none ${
                        formData.status === "inactive"
                          ? "border-blue-500 bg-blue-50 ring-2 ring-blue-500"
                          : "border-gray-300 bg-white"
                      }`}
                    >
                      <input
                        type="radio"
                        name="status"
                        value="inactive"
                        checked={formData.status === "inactive"}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <div className="flex w-full items-center justify-between">
                        <div className="flex items-center">
                          <div className="text-sm">
                            <span className="font-medium text-gray-900">
                              Inactive
                            </span>
                            <p className="text-gray-500">
                              Category will be hidden
                            </p>
                          </div>
                        </div>
                        <div
                          className={`h-4 w-4 rounded-full border-2 ${
                            formData.status === "inactive"
                              ? "border-blue-500 bg-blue-500"
                              : "border-gray-300"
                          }`}
                        ></div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Image Upload Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6 pb-3 border-b border-gray-200">
                Category Image
              </h2>

              <div className="flex flex-col items-center space-y-6">
                {/* Image Preview */}
                {preview ? (
                  <div className="relative">
                    <img
                      src={preview}
                      alt="Category preview"
                      className="w-64 h-48 object-cover rounded-2xl border-2 border-gray-300 shadow-lg"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors shadow-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="w-64 h-48 border-2 border-dashed border-gray-300 rounded-2xl flex flex-col items-center justify-center bg-gray-50">
                    <ImageIcon className="w-12 h-12 text-gray-400 mb-2" />
                    <p className="text-gray-500 text-center">
                      No image
                      <br />
                      <span className="text-sm">Click to upload</span>
                    </p>
                  </div>
                )}

                {/* Upload Button */}
                <label className="flex items-center gap-3 px-6 py-3 bg-blue-600 text-white rounded-xl cursor-pointer hover:bg-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg">
                  <Upload className="w-5 h-5" />
                  {preview ? "Change Image" : "Upload Image"}
                  <input
                    type="file"
                    name="image"
                    onChange={handleChange}
                    className="hidden"
                    accept="image/*"
                  />
                </label>

                <p className="text-sm text-gray-500 text-center max-w-md">
                  Recommended: 400x300px or larger, JPG, PNG, or WEBP format,
                  maximum size 2MB
                </p>
              </div>
            </div>

            {/* Current Image Info (for update) */}
            {isUpdating && subsingleCategory?.image && !preview && (
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
                <div className="flex items-center gap-3">
                  <ImageIcon className="w-5 h-5 text-blue-600" />
                  <div>
                    <h3 className="font-semibold text-blue-800">
                      Current Image
                    </h3>
                    <p className="text-blue-600 text-sm">
                      Existing image is saved. Upload a new image to replace it.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {isUpdating ? "Updating..." : "Saving..."}
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    {isUpdating ? "Update Category" : "Save Category"}
                  </>
                )}
              </button>

              {/* Form Status */}
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-500">
                  {isUpdating
                    ? `Updating category ID: ${uuid}`
                    : "A new category will be added to the system"}
                </p>
              </div>
            </div>
          </form>
        </div>
      </div>
    </NonAuthLayout>
  );
};

export default CreateUpdateSubCategory;
